import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Grid, IconButton, InputAdornment, Paper, SwipeableDrawer, TextField, Typography } from '@mui/material';
import FuseScrollbars from '@fuse/core/FuseScrollbars';

import { Icon, useSettings, useUserData, Utils } from 'app/main/components';
import withReducer from 'app/store/withReducer';
import { showAddNotesDialog } from 'app/main/submodules/notes/NotesAddDialog';
import NotesListItem from 'app/main/submodules/notes/NotesListItem';

import reducer from './store';
import { closeQuickNotesPanel, toggleQuickNotesPanel } from './store/stateSlice';

function QuickNotesPanel() {
	const dispatch = useDispatch<any>();
	const { settings, t } = useSettings('notes');
	const userData = useUserData();

	const state = useSelector(({ quickNotesPanel }: any) => quickNotesPanel.state);

	const searchRef = React.useRef<HTMLInputElement>(undefined);

	const [notes, setNotes] = React.useState([]);
	const [search, setSearch] = React.useState<string | undefined>('');
	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(value => !value);

	function openNoteDialog() {
		dispatch(
			showAddNotesDialog({
				model: { note_table: 'users', note_table_uid: userData.user_uid },
				updateRefresh,
				isUpdate: false
			})
		);
	}

	function handleSearch() {
		setSearch(Utils.formatSearchString(searchRef.current?.value));
	}

	function handleClose() {
		dispatch(closeQuickNotesPanel());
	}

	React.useEffect(() => {
		Utils.executeStored({
			action: 'st_notes_list',
			dispatch,
			params: {
				n_page: 1,
				n_rows: 20,
				filters: [
					{
						filt_field: 'note_table',
						filt_clause: 'equals',
						filt_value: 'users'
					},
					{
						filt_field: 'note_table_uid',
						filt_clause: 'equals',
						filt_value: userData.user_uid
					},
					{
						filt_field: 'note_search',
						filt_clause: 'like',
						filt_value: search
					}
				]
			},
			successHandler: response => {
				setNotes(response.data.list);
			}
		});
	}, [dispatch, refresh, search, userData.user_uid]);

	return (
		<SwipeableDrawer
			open={state}
			anchor="right"
			onOpen={_ev => {}}
			onClose={_ev => dispatch(toggleQuickNotesPanel())}
			disableSwipeToOpen
			sx={theme => ({
				'.MuiDrawer-paper': {
					backgroundColor: theme.palette.background.default,
					width: '100%',
					maxWidth: 400
				}
			})}
		>
			<FuseScrollbars className="p-16">
				<div className="flex flex-col">
					<div className="mb-8 flex items-center justify-between pt-4">
						<Typography className="text-28 font-semibold leading-none">{t('MY_NOTES_TITLE')}</Typography>
						<div>
							<Button startIcon={<Icon icon="text-box-plus-outline" />} onClick={openNoteDialog}>
								{t('DIALOG_ACTION_ADD')}
							</Button>
							<Button onClick={handleClose}>{t('DIALOG_ACTION_CLOSE')}</Button>
						</div>
					</div>
					{/* SEARCH */}
					<TextField
						className="mb-16"
						variant="outlined"
						size="small"
						placeholder={t('SEARCH_PLACEHOLDER')}
						InputProps={{
							inputProps: { ref: searchRef },
							classes: { root: 'p-0' },
							endAdornment: (
								<InputAdornment position="end">
									<IconButton size="small" onClick={handleSearch}>
										<Icon icon="magnify" />
									</IconButton>
								</InputAdornment>
							)
						}}
					/>
					{/* LIST */}
					<Grid container direction="column" spacing={1}>
						{notes &&
							notes.map((note: any) => (
								<Grid key={note.note_uid}>
									<Paper variant="outlined" className="rounded-6">
										<NotesListItem
											width={400}
											settings={settings}
											model={note}
											buttonProps={{ settings, model: note, updateRefresh }}
										/>
									</Paper>
								</Grid>
							))}
					</Grid>
				</div>
			</FuseScrollbars>
		</SwipeableDrawer>
	);
}

export default withReducer('quickNotesPanel', reducer)(React.memo(QuickNotesPanel));
